import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { ApiScreeningMatchProvider, customListSignificantFieldApi } from '../../../apis';
import { InternalApiEntityType } from '../../../api-internal';
import useCustomLists from '../../../custom-lists/useCustomLists';
import { useDispatch } from 'react-redux';
import { fetchSignificantFieldsSuccess } from '../../../custom-lists/customListsReducer';
import { createColumnHelper } from '@tanstack/react-table';
import useSchemaFields from '../../../custom-lists/useSchemaFields';
import Has from '../../../auth/Has';
import { CAN_MODIFY_CUSTOM_LIST_SIGNIFICANT_FIELD } from '../../../auth/permissions';
import { SalvDataTag, SalvTable, Size } from '@salvapp/ui-components';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import DowJonesSignificantFieldsModal from '../significantFields/DowJonesSignificantFieldsModal';
import { DowJonesCustomListCode, dowJonesListDisplayNames, dowJonesSortOrder } from '../ScreeningDataSetup';
import { useMe } from '../../../organisation/useMe';
import CustomListSuiSummary from './CustomListSuiSummary';

export interface SignificantFieldByField {
  fieldName: string;
  fieldLists: { listId: string; listName: string; fieldId: string; isSignificant: boolean }[];
}

interface Props {
  entityType: InternalApiEntityType;
  description?: ReactNode;
}

const ListSignificantFields = ({ entityType, description }: Props) => {
  const dispatch = useDispatch();
  const { features } = useMe();
  const isAdverseMediaEntitiesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES, features);

  const customLists = useCustomLists();
  useSchemaFields();
  const [significantFieldsByField, setSignificantFieldsByField] = useState<SignificantFieldByField[]>([]);

  const areSchemaFieldsFetched = customLists.some((customList) => customList.schemaFields);
  useEffect(() => {
    if (!areSchemaFieldsFetched) {
      return;
    }
    customListSignificantFieldApi
      .getSignificantFields({
        entityType,
      })
      .then((res) => {
        dispatch(fetchSignificantFieldsSuccess({ customListSignificantFields: res }));
      });
  }, [areSchemaFieldsFetched]);

  const areSignificantFieldsFetched = customLists.some((customList) =>
    customList.schemaFields?.some((field) => field.significantFor?.[entityType] !== undefined)
  );
  useEffect(() => {
    if (!areSignificantFieldsFetched) {
      return;
    }

    const newSignificantFieldsByField: SignificantFieldByField[] = [];
    customLists
      .filter((customList) => customList.code?.startsWith(ApiScreeningMatchProvider.DOW_JONES))
      .filter((customList) => customList.code !== DowJonesCustomListCode.DOW_JONES_AME || isAdverseMediaEntitiesEnabled)
      .toSorted(
        (a, b) =>
          dowJonesSortOrder.indexOf(a.code as DowJonesCustomListCode) -
          dowJonesSortOrder.indexOf(b.code as DowJonesCustomListCode)
      )
      .forEach((customList) => {
        customList.schemaFields
          ?.filter((schemaField) => !schemaField.parentId)
          .forEach((schemaField) => {
            const significantFieldByField = newSignificantFieldsByField.find(
              (significantFieldByField) => significantFieldByField.fieldName === schemaField.fieldName
            );

            const listField = {
              listId: customList.id!,
              listName: dowJonesListDisplayNames[customList.code as DowJonesCustomListCode],
              fieldId: schemaField.id!,
              isSignificant: schemaField.significantFor![entityType],
            };

            if (significantFieldByField) {
              significantFieldByField.fieldLists.push(listField);
            } else {
              newSignificantFieldsByField.push({
                fieldName: schemaField.fieldName,
                fieldLists: [listField],
              });
            }
          });
      });
    setSignificantFieldsByField(newSignificantFieldsByField);
  }, [areSignificantFieldsFetched, customLists]);

  const columnHelper = createColumnHelper<SignificantFieldByField>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('fieldName', {
        header: 'Field',
        cell: ({ getValue }) => <SalvDataTag value={getValue()} />,
      }),
      columnHelper.accessor('fieldLists', {
        header: 'List type',
        cell: ({ getValue }) => (
          <div style={{ display: 'flex', gap: '4px' }}>
            {getValue()
              .filter((list) => list.isSignificant)
              .map((list) => (
                <SalvDataTag key={list.listId} label={list.listName} />
              ))}
          </div>
        ),
      }),
    ],
    [customLists]
  );

  const dowJonesContent = (
    <>
      <div className='m-b-16' style={{ display: 'flex', gap: '12px' }}>
        <span className='title-s'>From Lists records</span>
        <Has allPermissions={CAN_MODIFY_CUSTOM_LIST_SIGNIFICANT_FIELD}>
          <DowJonesSignificantFieldsModal
            entityType={entityType}
            significantFieldsByField={significantFieldsByField}
            buttonSize={Size.EXTRA_SMALL}
          />
        </Has>
      </div>
      {description && <div className='m-b-16'>{description}</div>}
      <SalvTable
        size={Size.SMALL}
        columns={columns}
        data={significantFieldsByField.filter((significantFieldByField) =>
          significantFieldByField.fieldLists.some((list) => list.isSignificant)
        )}
        emptyMessage={'No Significant Update Indicators from List records have been configured'}
      />
    </>
  );

  const nonDjLists = customLists.filter((list) => !list.code?.startsWith(ApiScreeningMatchProvider.DOW_JONES));

  return (
    <>
      {dowJonesContent}
      {nonDjLists.length > 0 && (
        <div style={{ marginTop: '1.5rem', marginBottom: '64' }}>
          <span className='title-s m-b-16'>From Custom List records</span>
          <div className='m-t-16'>
            <CustomListSuiSummary nonDjLists={nonDjLists} entityType={entityType} />
          </div>
        </div>
      )}
    </>
  );
};

export default ListSignificantFields;
